export const getInfraredImageS3Key = ({
    orgId,
    projectId,
    flightId,
    fileId,
}: {
    orgId: string
    projectId: string
    flightId: string
    fileId: string
}) => `public/org/${orgId}/fire/${projectId}/flight/${flightId}/infrared/original/${fileId}`

export const getInfraredDownscaledImageS3Key = ({
    orgId,
    projectId,
    flightId,
    fileId,
}: {
    orgId: string
    projectId: string
    flightId: string
    fileId: string
}) => `public/org/${orgId}/fire/${projectId}/flight/${flightId}/infrared/downscaled/${fileId}`

export const orthoImage = (
    portfolioId: string | undefined,
    fireId: string | undefined,
    flightId: string | undefined,
    filename: string
) => `public/org/${portfolioId}/fire/${fireId}/flight/${flightId}/orthomosaic-input/${filename}`

export const sampleOrthoImage = (flightId: string | undefined, filename: string) =>
    `public/sample-data/flight/${flightId}/orthomosaic-input/${filename}`

export const sampleInfraredImagePath = (flightId: string | undefined, filename: string) =>
    `public/sample-data/flight/${flightId}/infrared/original/${filename}`

export const outputFolder = (
    portfolioId: string | undefined,
    fireId: string | undefined,
    flightId: string | undefined
) => `public/org/${portfolioId}/fire/${fireId}/flight/${flightId}/orthomosaic-output`

export const sampleOutputFolder = (flightId: string | undefined) =>
    `public/sample-data/flight/${flightId}/orthomosaic-output`

export const satelliteOutputFolder = (
    orgId: string | undefined,
    projectId: string | undefined,
    flightId: string | undefined
) => `public/org/${orgId}/fire/${projectId}/flight/${flightId}/satellite-output`

export const originalZipFilePath = (
    portfolioId: string | undefined,
    fireId: string | undefined,
    flightId: string | undefined
) => `public/org/${portfolioId}/fire/${fireId}/flight/${flightId}/infrared/original/images.zip`

export const liveVideoTranscodedFilePath = (
    portfolioId: string,
    fireId: string,
    flightId: string,
    recordingPrefix: string,
    extension = "mp4"
) => {
    const prefix = recordingPrefix.split("/").at(-1)
    return `public/org/${portfolioId}/fire/${fireId}/flight/${flightId}/video/${prefix}.${extension}`
}

export const sampleOriginalZipFilePath = (flightId: string | undefined) =>
    `public/sample-data/flight/${flightId}/infrared/original/images.zip`

// Folder (prefix) constructors — the canonical S3 directory each image type lives
// under. Image-path constructors below append a filename; listing operations use
// the folder directly as a prefix.
export const orthomosaicInputFolder = (orgId: string, projectId: string, flightId: string) =>
    `public/org/${orgId}/fire/${projectId}/flight/${flightId}/orthomosaic-input`

export const infraredOriginalFolder = (orgId: string, projectId: string, flightId: string) =>
    `public/org/${orgId}/fire/${projectId}/flight/${flightId}/infrared/original`

export const colorFinderImagesFolder = (orgId: string, projectId: string, flightId: string) =>
    `public/org/${orgId}/fire/${projectId}/flight/${flightId}/images`

export const epochOrthoKey = (
    orgId: string,
    projectId: string,
    flightId: string,
    epochId: string
) => `${outputFolder(orgId, projectId, flightId)}/${epochId}.tiff`

export const epochHotspotsKey = (
    orgId: string,
    projectId: string,
    flightId: string,
    epochId: string
) => `${outputFolder(orgId, projectId, flightId)}/${epochId}.geojson`

export const epochImagesKey = (
    orgId: string,
    projectId: string,
    flightId: string,
    epochId: string
) => `${outputFolder(orgId, projectId, flightId)}/${epochId}.images.json`

export const infraredImagePath = (
    orgId: string,
    projectId: string,
    flightId: string,
    filename: string
) => `${infraredOriginalFolder(orgId, projectId, flightId)}/${filename}`

export const colorFinderImagePath = (
    orgId: string,
    projectId: string,
    flightId: string,
    filename: string
) => `${colorFinderImagesFolder(orgId, projectId, flightId)}/${filename}`

export const rgbImagePath = (
    orgId: string,
    projectId: string,
    flightId: string,
    filename: string
) => `${orthomosaicInputFolder(orgId, projectId, flightId)}/${filename}`

export const panoramaImagePath = (
    orgId: string,
    projectId: string,
    flightId: string,
    filename: string
) => `public/org/${orgId}/fire/${projectId}/flight/${flightId}/images/${filename}`

export const isTiffFilename = (filename: string) => {
    const lower = filename.toLowerCase()
    return lower.endsWith(".tif") || lower.endsWith(".tiff")
}

export const rgbDownscaledImagePath = (
    orgId: string,
    projectId: string,
    flightId: string,
    filename: string
) =>
    `public/org/${orgId}/fire/${projectId}/flight/${flightId}/downscaled/${filename}${isTiffFilename(filename) ? ".jpg" : ""}`

export const iconImagePath = ({
    orgId,
    projectId,
    flightId,
    fileId,
}: {
    orgId: string
    projectId: string
    flightId: string // TODO: Change to entityId
    fileId: string
}) => `public/org/${orgId}/fire/${projectId}/flight/${flightId}/images/${fileId}`

export const iconDownscaledImagePath = ({
    orgId,
    projectId,
    flightId,
    fileId,
}: {
    orgId: string
    projectId: string
    flightId: string // not a flight lol
    fileId: string
}) => `public/org/${orgId}/fire/${projectId}/flight/${flightId}/images/${fileId}`

export const demPath = (root: string) => `${root}/odm_orthophoto/dem.tif`

export const arcOrthoKmzPath = (root: string, cropped: boolean) =>
    `${root}/odm_orthophoto/arc_ortho${cropped ? "_cropped" : ""}.kmz`

export const outputUrl = (
    portfolioId: string | undefined,
    fireId: string | undefined,
    chapterId: string | undefined,
    flightId: string | undefined
) =>
    `/api/organization/${portfolioId}/project/${fireId}/chapter/${chapterId}/flight/${flightId}/orthomosaicTiles`

export const finished = (root: string, extension = "tif", lr = false, cogId?: string) => {
    if (cogId) return `${root}/${cogId}`
    else return `${root}/odm_orthophoto/odm_orthophoto${lr ? "_LR" : ""}.${extension}`
}

export const tilemap = (root: string) => `${root}/orthophoto_tiles/tilemapresource.xml`

export const pilotTrackingFlightLogsPath = (
    orgId: string,
    fileFingerprint: string,
    fileName: string
) => `public/org/${orgId}/assets/pilot-tracking/flight-logs/${fileFingerprint}/${fileName}`

export const getProfilePhotoS3Key = ({ userId, fileId }: { userId: string; fileId: string }) =>
    `public/user/${userId}/profile-photo/${fileId}`

export const getCommentImageS3Key = ({
    orgId,
    projectId,
    entityId,
    fileId,
}: {
    orgId: string
    projectId: string
    entityId: string
    fileId: string
}) => `public/org/${orgId}/project/${projectId}/comments/${entityId}/${fileId}`

export const pdfRasterPath = (orgId: string, projectId: string, filename: string) =>
    `public/org/${orgId}/fire/${projectId}/pdf-rasters/${filename}`

export const pdfRasterCogPath = (orgId: string, projectId: string, filename: string) =>
    `${pdfRasterPath(orgId, projectId, filename)}.tif`
